/**
 * The public API exposed as window.__NEEDLE_INSPECTOR__.
 * All methods return plain JSON-serializable objects
 * so they work with Playwright's page.evaluate().
 */

import type { IApplication } from "../../core/src/editor/services/application/common/application.js";
import type { IHierarchyNode, IHierarchyService } from "../../core/src/platform/hierarchy/common/hierarchy.js";
import type { IAIToolService } from "../../core/src/platform/ai/common/ai.js";
import { aiTargetFor, resolveAiTarget, type AiTarget } from "../../core/src/platform/ai/common/target.js";
import type { IPropertyService, PropertyScope, PropertyViewMode } from "../../core/src/platform/properties/common/properties.js";
import type { IAnalyticsService } from "../../core/src/platform/analytics/common/analytics.js";
import { safeStringify } from "../../core/src/base/common/objects.js";

// Re-import enums as values (not just types)
import { PropertyScope as PropertyScopeEnum, PropertyViewMode as PropertyViewModeEnum } from "../../core/src/platform/properties/common/properties.js";


export interface SerializedNode {
    /** Pass this object verbatim to tools that accept a TARGET. */
    target?: AiTarget;
    name: string;
    type: string;
    label: string;
    visible: boolean;
    selected: boolean;
    expanded: boolean;
    childCount: number;
    children?: SerializedNode[];
}

export interface SerializedInspectorProperty {
    propertyPath: string;
    propertyKey: string;
    propertyLabel: string;
    propertyType: string;
    groupName: string;
    value?: unknown;
}

export interface FindPropertiesResult {
    error?: string;
    totalReturned: number;
    totalMatched: number;
    hasMore: boolean;
    results: Array<{
        target?: AiTarget;
        name: string;
        type: string;
        properties: SerializedInspectorProperty[];
    }>;
}

export interface ReadPropertyResult {
    error?: string;
    target?: AiTarget;
    name?: string;
    type?: string;
    propertyPath?: string;
    propertyKey?: string;
    propertyLabel?: string;
    propertyType?: string;
    value?: unknown;
    metadata?: unknown;
}

export interface FindPropertiesArgs {
    nodeQuery?: string;
    /** TARGETS returned by search/resource/property tools, passed back verbatim. */
    targets?: AiTarget[];
    propertyQuery?: string;
    includeValues?: boolean;
    maxValueSize?: number;
    maxResults?: number;
}

export interface OpenResourceResult {
    error?: string;
    success: boolean;
    target?: string;
    targetType?: "auto" | "url" | "fsPath";
    resolvedTarget?: string;
    kind?: "url" | "fsPath";
}

export interface ToolDefinition {
    name: string;
    description: string;
    inputSchema: object;
}

export interface NeedleInspectorAPI {
    /** True once the inspector has detected a Three.js scene and is ready to use. */
    ready: boolean;

    /** List all available inspector tools with their names, descriptions, and input schemas. */
    getTools(): Promise<ToolDefinition[]>;

    /** Call any tool by name with optional arguments. Returns the tool's result. */
    callTool(name: string, args?: object): Promise<any>;

    /** Get the full scene hierarchy as a serialized tree. */
    getHierarchy(maxDepth?: number): Promise<SerializedNode[]>;

    /** Open a resource in the editor by URL or absolute local path. */
    openResource(target: string, targetType?: "auto" | "url" | "fsPath"): Promise<OpenResourceResult>;

    /** Get the currently selected nodes. */
    getSelectedNodes(): Promise<SerializedNode[]>;

    /** Select a node by a TARGET returned from this API. */
    selectNode(target: AiTarget): Promise<{ success: boolean; message?: string }>;

    /** Search for nodes by name. */
    searchNodes(query: string, maxResults?: number): Promise<SerializedNode[]>;

    /** Get properties of an object by its TARGET. */
    getProperties(target: AiTarget): Promise<FindPropertiesResult>;

    /** Find properties matching a query across nodes. Shorthand for the property_find tool. */
    findProperties(args: FindPropertiesArgs): Promise<FindPropertiesResult>;

    /** Read a specific property value. Shorthand for the property_read tool. */
    readProperty(target: AiTarget, propertyPath: string): Promise<ReadPropertyResult>;
}


function serializeNode(hierarchy: IHierarchyService, node: IHierarchyNode, depth: number, maxDepth: number): SerializedNode {
    const result: SerializedNode = {
        target: node.owner && typeof node.owner === "object"
            ? aiTargetFor(undefined, hierarchy, node.owner) ?? undefined
            : undefined,
        name: node.name,
        type: node.type,
        label: node.label,
        visible: node.visible,
        selected: node.selected,
        expanded: node.expanded,
        childCount: node.children.length,
    };

    if (depth < maxDepth && node.children.length > 0) {
        result.children = node.children.map(child => serializeNode(hierarchy, child, depth + 1, maxDepth));
    }

    return result;
}


export function createInspectorAPI(app: IApplication, analytics?: IAnalyticsService): NeedleInspectorAPI {

    const hierarchy = app.hierarchyService;
    const toolService = app.ai?.toolService;
    const propertyService = app.propertyService;

    const api: NeedleInspectorAPI = {

        ready: false,

        async getTools(): Promise<ToolDefinition[]> {
            if (!toolService) return [];
            const tools = await toolService.getAvailableTools();
            return tools.map(t => ({
                name: t.name,
                description: t.description,
                inputSchema: t.parametersJSONSchema,
            }));
        },

        async callTool(name: string, args?: object): Promise<any> {
            if (!toolService) return { error: "Tool service not available" };
            try {
                analytics?.logEvent("agent.callTool", { tool: name });
                const result = await toolService.callTool(name, args || {});
                // Parse aiMessage if it's a JSON string, otherwise return as-is
                if (result.result.aiMessage) {
                    try {
                        return JSON.parse(result.result.aiMessage);
                    } catch {
                        return result.result.aiMessage;
                    }
                }
                return result.result.message || result.result;
            } catch (err: any) {
                console.warn(`[Needle Inspector] callTool('${name}') failed:`, err);
                analytics?.logEvent("agent.callTool.error", { tool: name, error: err?.message || String(err) });
                return { error: err?.message || String(err) };
            }
        },

        async getHierarchy(maxDepth: number = 10): Promise<SerializedNode[]> {
            if (!hierarchy) return [];
            analytics?.logEvent("agent.getHierarchy");
            return hierarchy.roots.map(root => serializeNode(hierarchy, root, 0, maxDepth));
        },

        async openResource(target: string, targetType: "auto" | "url" | "fsPath" = "auto"): Promise<OpenResourceResult> {
            return api.callTool("resource_open", { target, targetType });
        },

        async getSelectedNodes(): Promise<SerializedNode[]> {
            if (!hierarchy) return [];
            const selection = hierarchy.getSelectedNodes();
            return selection.map(node => serializeNode(hierarchy, node, 0, 2));
        },

        async selectNode(target: AiTarget): Promise<{ success: boolean; message?: string }> {
            if (!hierarchy) return { success: false, message: "Hierarchy not available" };
            const owner = resolveAiTarget(undefined, hierarchy, target);
            const node = owner ? hierarchy.find(owner) : null;
            if (!node) return { success: false, message: "TARGET did not resolve to a hierarchy node" };
            node.selected = true;
            return { success: true };
        },

        async searchNodes(query: string, maxResults: number = 20): Promise<SerializedNode[]> {
            if (!hierarchy) return [];
            analytics?.logEvent("agent.searchNodes", { query });
            const results = await hierarchy.search(query, maxResults);
            return (results ?? []).map(node => serializeNode(hierarchy, node, 0, 1));
        },

        async getProperties(target: AiTarget): Promise<FindPropertiesResult> {
            return api.callTool("property_find", {
                targets: [target],
                includeValues: true,
                maxValueSize: 500,
            });
        },

        async findProperties(args: FindPropertiesArgs): Promise<FindPropertiesResult> {
            return api.callTool("property_find", args);
        },

        async readProperty(target: AiTarget, propertyPath: string): Promise<ReadPropertyResult> {
            return api.callTool("property_read", { target, propertyPath });
        },
    };

    return api;
}
